import { Box, Stack, Tooltip, Typography } from "@mui/material";
import React, { useState } from "react";
import appConfig from "src/config/env";
import convertCurrency, { convertIntMoney } from "src/helper/convertCurrency";
import useProductList from "src/hooks/product/useProductList";
import useQueryParams from "src/hooks/useQueryParams";
import { v4 as uuid } from "uuid";

export default function useProductBody({ searchBy, keyword, productSelectList, paramsQuery, productTab, searchValue }) {
  const [option, setOption] = useQueryParams();
  const [pagi, setPagi] = useState({
    skip: 0,
    limit: 50,
  });
  const { data: product, isLoading } = useProductList({
    ...pagi,
    searchField: !!searchBy ? productSelectList[searchBy - 1]?.value : productSelectList[0]?.value,
    searchValue: !!keyword ? keyword : undefined,
    sortField: "createdAt",
    sortOrder: "asc",
    productGroup: option?.tab === "product" ? productTab[0]?.id : option?.tab,
    ...paramsQuery,
  });
  const item = productTab?.find((v) => v?.id === option?.tab);

  const handleProductUpdate = (item) => {
    setOption({ ...option, productId: item?._id, status: "update" });
  };

  let bodyList = Array.isArray(product?.data)
    ? product?.data?.map((v, index) => ({
        ...v,
        no: index + 1,
        productDetail: (
          <Box
            onClick={() => handleProductUpdate(v)}
            display="flex"
            gap="6px"
            sx={{ cursor: "pointer" }}
          >
            <Box
              sx={{
                minWidth: "66px",
                minHeight: "66px",
                maxWidth: "66px",
                maxHeight: "66px",
                border: "1px solid #cccccc",
                borderRadius: "8px",
                backgroundColor: "white",
                background: !!v?.image?.src ? `url(${appConfig.API_FILE}/${v?.image?.src}) center / cover no-repeat` : "",
              }}
            />
            <Box
              textAlign="left"
              flex={1}
            >
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ maxWidth: "400px", width: "100%" }}
              >
                {v?.name ?? "-"}
              </Typography>
              <Typography variant="body2">{v?.nameVi ?? "-"}</Typography>
              <Typography variant="body2">{v?.code ?? "-"}</Typography>
            </Box>
          </Box>
        ),
        classification:
          Array.isArray(v?.productModels) && !!v?.productModels?.length ? (
            <Box
              sx={{
                background: "var(--grey)",
                borderRadius: "100px",
                display: "inline-flex",
                p: "6px 12px",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              {v?.productModels?.map((val, index) => {
                return (
                  <Typography
                    variant="body2"
                    key={val?._id}
                  >
                    {val?.name ?? "-"}
                    {index !== v?.productModels?.length - 1 && ", "}
                  </Typography>
                );
              })}
            </Box>
          ) : (
            "N/A"
          ),
        brandName: v?.brand?.name ?? "",
        pcb: v?.notificationNo,
        col1: "",
        includesRow: v?.productModels?.map((val, i) => ({
          col: <></>,
          product: (
            <Box
              display="flex"
              gap="12px"
              width="120px"
            >
              <Box
                sx={{
                  width: "46px",
                  height: "46px",
                  minWidth: "46px",
                  ml: "20px",
                  border: "1px solid #cccccc",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  background: !!val?.image?.src ? `url(${appConfig.API_FILE}/${val?.image?.src}) center / cover no-repeat` : "var(--light-grey)",
                }}
              />
              <Box
                flex={1}
                textAlign="left"
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                >
                  {val?.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {val?.sku ? `SKU: ${val?.sku}` : "-"}
                </Typography>
              </Box>
            </Box>
          ),
          col1: <></>,
          col5: <></>,
          col2: <></>,
          barcode: val?.barcode,
          col4: <></>,
        })),
      }))
    : [];

  if (!!item && item?.tab !== 1) {
    bodyList = Array.isArray(product?.data)
      ? product?.data?.map((v, i) => ({
          ...v,
          no: i + 1,
          productName: (
            <Stack
              direction="row"
              alignItems="flex-start"
              gap={1}
            >
              <Box
                sx={{
                  minWidth: "66px",
                  minHeight: "66px",
                  maxWidth: "66px",
                  maxHeight: "66px",
                  border: "1px solid #cccccc",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  background: !!v?.image?.src ? `url(${appConfig.API_FILE}/${v?.image?.src}) center / cover no-repeat` : "url(/img/broke-img.png) center / cover no-repeat",
                }}
              />
              <Box
                textAlign="left"
                display="flex"
                flexDirection="column"
              >
                <Typography
                  whiteSpace="nowrap"
                  fontWeight="bold"
                >
                  {v?.name ?? "-"}
                </Typography>
                <Typography
                  variant="caption"
                  whiteSpace="nowrap"
                >
                  {v?.shortName ?? "-"}
                </Typography>
                <Typography variant="caption">{v?.code ?? "-"}</Typography>
              </Box>
            </Stack>
          ),
          internalName: v?.shortName,
          manufacturerName: (
            <Tooltip title={v?.manufacturer?.name}>
              <Typography
                variant="body2"
                mx="auto"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  maxWidth: "100px",
                }}
              >
                {v?.manufacturer?.name ?? "-"}
              </Typography>
            </Tooltip>
          ),
          describe: (
            <Tooltip title={v?.description}>
              <Typography
                variant="body2"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  maxWidth: "100px",
                }}
              >
                {v?.description}
              </Typography>
            </Tooltip>
          ),
          col1: <></>,
          col2: <></>,
          includesRow: v?.productModels?.map((val, i) => ({
            col: <></>,
            product: (
              <Box
                display="flex"
                gap="12px"
                width="120px"
              >
                <Box
                  sx={{
                    width: "46px",
                    height: "46px",
                    minWidth: "46px",
                    ml: "20px",
                    border: "1px solid #cccccc",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    background: !!val?.image?.src ? `url(${appConfig.API_FILE}/${val?.image?.src}) center / cover no-repeat` : "url(/img/broke-img.png) center / cover no-repeat",
                  }}
                />
                <Box
                  flex={1}
                  textAlign="left"
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "nowrap" }}
                    fontWeight="bold"
                  >
                    {val?.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {val?.sku ?? val?.code ?? "-"}
                  </Typography>
                </Box>
              </Box>
            ),
            col1: <></>,
            col5: <></>,
            col7: val?.barcode,
            col2: val?.name,
            col4: (
              <Box>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                >
                  {convertIntMoney(val?.price ?? 0)}đ
                </Typography>
                <Typography
                  variant="caption"
                  color="initial"
                  fontWeight="bold"
                >
                  ({convertCurrency(val?.price ?? 0)} đồng)
                </Typography>
              </Box>
            ),
            col6: <></>,
          })),
        }))
      : [];
  }

  let sortData = ["no", "productDetail", "classification", "brandName", "pcb", "col1"];

  if (!!item && item?.tab !== 1) {
    sortData = ["no", "productName", "internalName", "manufacturerName", "describe", "col1", "col2"];
  }

  let header = [
    {
      id: uuid(),
      title: "STT",
      align: "center",
    },
    {
      id: uuid(),
      title: "Sản phẩm",
      align: "left",
    },
    {
      id: uuid(),
      title: "Phân loại",
      align: "center",
    },
    {
      id: uuid(),
      title: "Thương hiệu/NSX",
      align: "center",
    },
    {
      id: uuid(),
      title: "Số PCB",
      align: "center",
    },
    {
      id: uuid(),
      title: "Mã vạch",
      align: "center",
    },
    {
      id: uuid(),
      title: "Thao tác",
      align: "center",
    },
  ];

  if (!!item && item?.tab !== 1) {
    header = [
      {
        id: uuid(),
        title: "STT",
        align: "center",
      },
      {
        id: uuid(),
        title: "Tên thương mại",
        align: "left",
      },
      {
        id: uuid(),
        title: "Tên nội bộ",
        align: "center",
      },
      {
        id: uuid(),
        title: "Nhà sản xuất",
        align: "center",
      },
      {
        id: uuid(),
        title: "Mô tả kỹ thuật",
        align: "left",
      },
      {
        id: uuid(),
        title: "Phân loại",
        align: "center",
      },
      {
        id: uuid(),
        title: "Giá nhập trước VAT",
        align: "center",
      },
      {
        id: uuid(),
        title: "Thao tác",
        align: "center",
      },
    ];
  }
  return { bodyList, isLoading, sortData, header, total: product?.count, pagi, setPagi };
}
